// Manages scroll and time window behavior for the calendar.

import { isToday } from 'date-fns';
import useCurrentTime from '@/features/tasks/hooks/status/use-task-current-time.js';
import { CALENDAR_START_HOUR, CALENDAR_END_HOUR } from '@/config/constants';

const MOBILE_EVENING_START_HOUR = 22;

const handleMobileLogic = (currentHour, hourHeight, startHour) => {
  if (currentHour >= MOBILE_EVENING_START_HOUR) {
    return {
      startHour: 18,
      endHour: CALENDAR_END_HOUR,
      scrollPosition: 0,
      scrollBehavior: 'auto',
    };
  }
  if (currentHour < CALENDAR_START_HOUR) {
    return {
      startHour: CALENDAR_START_HOUR,
      endHour: CALENDAR_END_HOUR,
      scrollPosition: 0,
      scrollBehavior: 'auto',
    };
  }
  const scrollTarget = Math.max(CALENDAR_START_HOUR, currentHour - 1);
  return {
    startHour,
    endHour: CALENDAR_END_HOUR,
    scrollPosition: (scrollTarget - startHour) * hourHeight,
    scrollBehavior: 'auto',
  };
};

const handleDesktopLogic = (hourHeight, nowLinePosition) => {
  if (hourHeight === undefined || !Number.isFinite(hourHeight))
    return { scrollPosition: -1, scrollBehavior: 'smooth' };
  const safeNowLine = Math.max(0, nowLinePosition);
  if (safeNowLine > hourHeight) {
    return {
      scrollPosition: safeNowLine - hourHeight,
      scrollBehavior: 'smooth',
    };
  }
  return { scrollPosition: 0, scrollBehavior: 'smooth' };
};

/**
 * Computes initial scroll position for non-today dates.
 * @returns {{startHour: number, endHour: number, scrollPosition: number, scrollBehavior: string}}
 */
const getDefaultViewSettings = () => ({
  startHour: CALENDAR_START_HOUR,
  endHour: CALENDAR_END_HOUR,
  scrollPosition: 0,
  scrollBehavior: 'auto',
});

/**
 * Custom hook to determine the dynamic display hours and scroll position for the calendar view.
 *
 * This hook operates as a state-machine with 3 main branches:
 * 1. **Today + Mobile**: Dynamically adjusts `startHour` and `endHour` based on the current time
 *    to create a "window" of visible hours. `scrollBehavior` is set to 'auto' for instant jumps.
 * 2. **Today + Desktop**: Keeps the standard CALENDAR_START_HOUR - CALENDAR_END_HOUR range but calculates the `scrollPosition`
 *    to center the "now" line. If the current time is outside the range, auto-scrolling is disabled.
 * 3. **Not Today (Future/Past)**: Defaults to showing CALENDAR_START_HOUR - CALENDAR_END_HOUR, scrolled to the top (0px).
 *
 * ### The `-1` Sentinel Value
 * The `scrollPosition` returns `-1` to indicate that the caller should **not** automatically scroll.
 * This allows the user to manually scroll without the calendar forcing them back to a specific position.
 *
 * @param {boolean} isMobile - Flag indicating if the view is mobile.
 * @param {Array} days - The array of day objects being displayed.
 * @param {number} nowLinePosition - The pixel position of the "now" line.
 * @param {number} slotHeight - The height of a single 15-minute slot in pixels.
 * @returns {{startHour: number, endHour: number, scrollPosition: number, scrollBehavior: string}}
 */
const useCalendarViewLogic = (isMobile, days, nowLinePosition, slotHeight) => {
  const now = useCurrentTime();
  const currentHour = now.getHours();
  const hourHeight = slotHeight * 4;

  let startHour = CALENDAR_START_HOUR;
  let endHour = CALENDAR_END_HOUR;
  let scrollPosition = -1;
  let scrollBehavior = 'smooth';

  const isCalendarShowingToday =
    days && days.length > 0 && isToday(days[0].date);

  if (isCalendarShowingToday) {
    scrollBehavior = 'auto';
    if (isMobile) {
      const mobileResult = handleMobileLogic(
        currentHour,
        hourHeight,
        startHour,
      );
      startHour = mobileResult.startHour;
      endHour = mobileResult.endHour;
      scrollPosition = mobileResult.scrollPosition;
    } else {
      // Desktop logic
      if (
        currentHour >= CALENDAR_END_HOUR ||
        currentHour < CALENDAR_START_HOUR
      ) {
        scrollPosition = -1;
      } else {
        const desktopResult = handleDesktopLogic(hourHeight, nowLinePosition);
        scrollPosition = desktopResult.scrollPosition;
        // Use smooth scrolling for desktop when actively positioning
        scrollBehavior = 'smooth';
      }
    }
  } else if (days && days.length > 0) {
    const defaultSettings = getDefaultViewSettings();
    startHour = defaultSettings.startHour;
    endHour = defaultSettings.endHour;
    scrollPosition = defaultSettings.scrollPosition;
    scrollBehavior = defaultSettings.scrollBehavior;
  }

  return { startHour, endHour, scrollPosition, scrollBehavior };
};

export default useCalendarViewLogic;
